import React, { useState, useMemo } from 'react';
import { Table as TableIcon, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';

export interface TableWidgetConfig {
  title: string;
  data: Array<{ label: string; [key: string]: string | number }>;
  series: string[];
  showSearch: boolean;
  rowsPerPage: number;
  backgroundColor?: string;
}

interface TableWidgetProps {
  config: TableWidgetConfig;
  width: number;
  height: number;
}

function isLightBackground(hexColor?: string): boolean {
  if (!hexColor) return true;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
}

export function TableWidget({ config, width, height }: TableWidgetProps) {
  const {
    title = 'Untitled Table',
    data = [],
    series = [],
    showSearch = false,
    rowsPerPage = 10,
    backgroundColor,
  } = config || {};

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const area = width * height;
  const isSmall = area <= 24;
  const isMedium = area <= 48;

  const titleSize = isSmall ? 'text-sm' : isMedium ? 'text-base' : 'text-lg';
  const tableTextSize = isSmall ? 'text-xs' : 'text-sm';
  const padding = isSmall ? 'p-2' : isMedium ? 'p-3' : 'p-4';

  const isLight = isLightBackground(backgroundColor);
  const titleTextColor = backgroundColor ? (isLight ? 'text-gray-900' : 'text-white') : 'text-dashboard-primary';
  const textColor = backgroundColor ? (isLight ? 'text-gray-700' : 'text-gray-200') : 'text-gray-700 dark:text-gray-200';
  const headerBg = backgroundColor ? (isLight ? 'bg-gray-100' : 'bg-gray-700') : 'bg-gray-100 dark:bg-gray-700';
  const borderColor = backgroundColor ? (isLight ? 'border-gray-200' : 'border-gray-600') : 'border-gray-200 dark:border-gray-600';

  // All hooks must be called before any early returns
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [data, searchQuery]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIdx, startIdx + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp size={12} className="inline ml-1" />
    ) : (
      <ArrowDown size={12} className="inline ml-1" />
    );
  };

  // Check for empty data after all hooks
  if (!data || data.length === 0) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <TableIcon size={48} className="text-gray-300 dark:text-gray-600 mb-2" />
        <div className="text-gray-400 dark:text-gray-500 text-sm text-center">
          Click "Edit Data" to add table content
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col overflow-hidden`}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className={`${titleSize} font-semibold ${titleTextColor} mb-2`}>
        {title}
      </div>

      {showSearch && (
        <div className="mb-2">
          <div className="relative">
            <Search size={16} className="absolute left-2 top-2 text-gray-400" />
            <input
              type="search"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-8 pr-3 py-1 text-xs border ${borderColor} rounded bg-white dark:bg-gray-800 ${textColor}`}
            />
          </div>
          {searchQuery && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Showing {sortedData.length} of {data.length} rows
            </p>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto rounded-lg">
        {sortedData.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
            No results found
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-dashboard-widget">
            <table className={`w-full ${tableTextSize}`}>
              <thead className={`${headerBg} sticky top-0`}>
                <tr>
                  <th
                    className={`px-3 py-2 text-left font-semibold ${textColor} cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors select-none`}
                    onClick={() => handleSort('label')}
                  >
                    <div className="flex items-center gap-1">
                      Label {getSortIndicator('label')}
                    </div>
                  </th>
                  {series.map((col) => (
                    <th
                      key={col}
                      className={`px-3 py-2 text-left font-semibold ${textColor} cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors select-none`}
                      onClick={() => handleSort(col)}
                    >
                      <div className="flex items-center gap-1">
                        {col} {getSortIndicator(col)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-dashboard-widget">
                {paginatedData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx % 2 === 0
                        ? 'bg-white dark:bg-gray-800'
                        : 'bg-gray-50 dark:bg-gray-850'
                    } hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors ${textColor}`}
                  >
                    <td className="px-3 py-2 font-medium">{row.label}</td>
                    {series.map((col) => (
                      <td key={col} className="px-3 py-2">
                        {typeof row[col] === 'number' ? row[col].toLocaleString() : row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-dashboard-widget">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50'
              }`}
            >
              <ChevronLeft size={14} className="inline mr-1" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50'
              }`}
            >
              Next
              <ChevronRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
